import * as React from 'react';

import {
  CalendarDateTime,
  getLocalTimeZone,
  today,
  toZoned,
} from '@internationalized/date';
import {
  enumOperationType,
  OperationType,
  PricingMode,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  ContentSection,
  Radio,
  RadioGroupField,
  TextField,
} from '@rematter/ui-web';
import { SmartDatePicker } from '@rematter/ui-web/components';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';
import { useLocation } from 'wouter';

import { customPriceListCopyFromAtom } from '../../_graphql/mutationCustomPriceListLifecycle';
import * as state from '../../_state/customPriceListsList.state';
import { useTranslations } from '../../i18n/useTranslations';

import { SelectCounterpartyGroup } from '~/shared/components/select-fields/SelectCounterpartyGroup';
import { SelectCurrency } from '~/shared/components/select-fields/SelectCurrency';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { useUserDefaultFacilityId } from '~/shared/hooks/useUserFacilities';

type InitialValues = {
  name?: string;
  operationType?: string;
  pricingMode?: PricingMode;
  currency?: string;
  counterpartyGroupId?: string;
  facilityId?: string;
};

interface CustomPriceListDraftDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  copyFromDefaultPriceListId?: string;
  copyFromCustomPriceListId?: string;
  initialValues?: InitialValues;
}

//
// ⚛️ ⚛️ ⚛️ CUSTOM PRICE LIST DRAFT DIALOG ⚛️ ⚛️ ⚛️
//
export const CustomPriceListDraftDialog = ({
  isOpen,
  onClose,
  onSuccess,
  copyFromDefaultPriceListId,
  copyFromCustomPriceListId,
  initialValues,
}: CustomPriceListDraftDialogProps) => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, navigate] = useLocation();

  // States
  const [name, setName] = React.useState('');
  const [operationType, setOperationType] = React.useState<string>('Purchase');
  const [pricingMode, setPricingMode] = React.useState<PricingMode>('Standard');
  const [currency, setCurrency] = React.useState('USD');
  const [counterpartyGroupId, setCounterpartyGroupId] = React.useState('');
  const [facilityId, setFacilityId] = React.useState<string | undefined>(
    undefined,
  );
  const [startDate, setStartDate] = React.useState<
    import('@internationalized/date').CalendarDate | null
  >(() => today(getLocalTimeZone()));
  const [endDate, setEndDate] = React.useState<
    import('@internationalized/date').CalendarDate | null
  >(null);
  const [notes, setNotes] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Data hooks
  const toast = useToast();
  const draftCustomPriceList = useSetAtom(state.$draftCustomPriceList);
  const copyFrom = useSetAtom(customPriceListCopyFromAtom({}));
  const userDefaultFacilityId = useUserDefaultFacilityId();

  // Callbacks
  const handleClose = React.useCallback(() => {
    setName('');
    setOperationType('Purchase');
    setPricingMode('Standard');
    setCurrency('USD');
    setCounterpartyGroupId('');
    setFacilityId(undefined);
    setStartDate(today(getLocalTimeZone()));
    setEndDate(null);
    setNotes('');
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (!name.trim()) {
      toast.error(t.pricing.customPriceLists.validation.nameRequired);
      return;
    }
    if (!counterpartyGroupId.trim()) {
      toast.error(
        t.pricing.customPriceLists.validation.counterpartyGroupRequired,
      );
      return;
    }
    if (!facilityId) {
      toast.error(t.pricing.customPriceLists.validation.facilityRequired);
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await draftCustomPriceList({
        facilityId,
        name: name.trim(),
        counterpartyGroupId,
        currency,
        operationType: operationType as OperationType,
        pricingMode,
        startDate: startDate
          ? toZoned(
              new CalendarDateTime(
                startDate.year,
                startDate.month,
                startDate.day,
              ),
              getLocalTimeZone(),
            ).toAbsoluteString()
          : null,
        endDate: endDate
          ? toZoned(
              new CalendarDateTime(
                endDate.year,
                endDate.month,
                endDate.day,
                23,
                59,
                59,
              ),
              getLocalTimeZone(),
            ).toAbsoluteString()
          : null,
        notes: notes.trim() || null,
      });
      const newId = result?.pricingCustomPriceListDraft?.id;
      if (newId && copyFromDefaultPriceListId) {
        await copyFrom({
          targetCustomPriceListId: newId,
          sourceDefaultPriceListId: copyFromDefaultPriceListId,
        });
      }
      if (newId && copyFromCustomPriceListId) {
        await copyFrom({
          targetCustomPriceListId: newId,
          sourceCustomPriceListId: copyFromCustomPriceListId,
        });
      }
      toast.success(t.pricing.customPriceLists.toast.created);
      onSuccess?.();
      handleClose();
      if (newId && (copyFromDefaultPriceListId || copyFromCustomPriceListId)) {
        navigate(`/pricing/lists/${newId}`);
      }
    } catch (err) {
      toast.error(
        t.pricing.customPriceLists.toast.createError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    name,
    counterpartyGroupId,
    facilityId,
    currency,
    operationType,
    pricingMode,
    startDate,
    endDate,
    notes,
    toast,
    t,
    onSuccess,
    handleClose,
    draftCustomPriceList,
    copyFromDefaultPriceListId,
    copyFromCustomPriceListId,
    copyFrom,
    navigate,
  ]);

  // Effects
  React.useEffect(() => {
    if (isOpen && initialValues) {
      if (initialValues.name) setName(initialValues.name);
      if (initialValues.operationType)
        setOperationType(initialValues.operationType);
      if (initialValues.pricingMode) setPricingMode(initialValues.pricingMode);
      if (initialValues.currency) setCurrency(initialValues.currency);
      if (initialValues.counterpartyGroupId)
        setCounterpartyGroupId(initialValues.counterpartyGroupId);
      if (initialValues.facilityId) setFacilityId(initialValues.facilityId);
    } else if (
      isOpen &&
      !initialValues &&
      !facilityId &&
      userDefaultFacilityId
    ) {
      setFacilityId(userDefaultFacilityId);
    }
  }, [isOpen, initialValues, userDefaultFacilityId, facilityId]);

  const canSubmit =
    Boolean(name.trim()) &&
    Boolean(counterpartyGroupId.trim()) &&
    Boolean(facilityId) &&
    !isSubmitting;

  return (
    <ConsoleDialog
      className="@CustomPriceListDraftDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.pricing.customPriceLists.create.title}
      contentClassName="px-0"
      content={
        <>
          <p className="px-5 pb-5 text-sm text-neutral/60">
            {t.pricing.customPriceLists.create.draftNote}
          </p>
          <ContentSection
            variant="panel"
            title={t.pricing.customPriceLists.create.sections.details}
          >
            <div className="grid gap-4">
              <SelectEnum
                enumValue={enumOperationType}
                label={t.pricing.customPriceLists.create.fields.operationType}
                value={operationType}
                onChange={(value) => setOperationType(value ?? 'Purchase')}
                isRequired
              />
              <TextField
                label={t.pricing.customPriceLists.create.fields.name}
                value={name}
                onChange={setName}
                placeholder={
                  t.pricing.customPriceLists.create.placeholders.name
                }
                isRequired
              />
              <SelectFacility
                label={t.pricing.customPriceLists.create.fields.facility}
                value={facilityId}
                onChange={(value) => setFacilityId(value ?? undefined)}
                placeholder={
                  t.pricing.customPriceLists.create.placeholders.facility
                }
                isRequired
              />
              <SelectCounterpartyGroup
                label={
                  t.pricing.customPriceLists.create.fields.counterpartyGroup
                }
                value={counterpartyGroupId || undefined}
                onChange={(value) => setCounterpartyGroupId(value ?? '')}
                placeholder={
                  t.pricing.customPriceLists.create.placeholders
                    .counterpartyGroup
                }
                isRequired
              />

              <RadioGroupField
                label={t.pricing.customPriceLists.create.fields.pricingMode}
                value={pricingMode}
                onChange={(value) => setPricingMode(value as PricingMode)}
                orientation="vertical"
              >
                <Radio value="Standard">
                  <div>
                    <div>
                      {
                        t.pricing.customPriceLists.create.fields
                          .pricingModeStandard
                      }
                    </div>
                    <div className="text-xs text-neutral/60">
                      {
                        t.pricing.customPriceLists.create.fields
                          .pricingModeStandardDescription
                      }
                    </div>
                  </div>
                </Radio>
                <Radio value="CRV">
                  <div>
                    <div>
                      {t.pricing.customPriceLists.create.fields.pricingModeCRV}
                    </div>
                    <div className="text-xs text-neutral/60">
                      {
                        t.pricing.customPriceLists.create.fields
                          .pricingModeCRVDescription
                      }
                    </div>
                  </div>
                </Radio>
              </RadioGroupField>
              <SelectCurrency
                label={t.pricing.customPriceLists.create.fields.currency}
                value={currency}
                onChange={(value) => setCurrency(value ?? 'USD')}
                placeholder={
                  t.pricing.customPriceLists.create.placeholders.currency
                }
              />
            </div>
          </ContentSection>

          <ContentSection
            variant="panel"
            title={t.pricing.customPriceLists.create.sections.dates}
          >
            <div className="grid grid-cols-2 gap-4">
              <SmartDatePicker
                label={t.pricing.customPriceLists.create.fields.startDate}
                value={startDate ?? undefined}
                onChange={(date) => setStartDate(date ?? null)}
              />
              <SmartDatePicker
                label={t.pricing.customPriceLists.create.fields.endDate}
                value={endDate ?? undefined}
                onChange={(date) => setEndDate(date ?? null)}
              />
            </div>
          </ContentSection>

          <ContentSection
            variant="panel"
            hideDivider
            title={t.common.sections.additionalInfo}
          >
            <TextField
              label={t.pricing.customPriceLists.create.fields.notes}
              value={notes}
              onChange={setNotes}
              placeholder={t.pricing.customPriceLists.create.placeholders.notes}
            />
          </ContentSection>
        </>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.common.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {isSubmitting
              ? t.common.loading.creating
              : t.pricing.customPriceLists.create.submitButton}
          </Button>
        </div>
      }
    />
  );
};
